'use client';
import { SvgCheck } from '@icons/designSystemIcons/SvgCheck';
import { priceFormat } from '@utils/priceFormat';
import { useIsMobileDevice } from 'app/hooks/useIsMobileDevice';
import { Product } from 'app/types/product';
import { Container, Flex } from 'designSystem/Layouts/Layouts';
import { Text, Title } from 'designSystem/Texts/Texts';
import Image from 'next/image';

import ProductSelectorButton from './ProductSelectorButton';
import { SURGERY_DATA } from './surgeryData';

export default function ProductPricesSurgerySSR({
  product,
  showButton = true,
  isMobile,
}: {
  product: Product;
  showButton?: boolean;
  /** Lo calcula el Server Component padre con `isMobileSSR()`. */
  isMobile?: boolean;
}) {
  const detectedIsMobile = useIsMobileDevice();
  const isMobileDevice = isMobile ?? detectedIsMobile;

  const data = SURGERY_DATA.find(
    item => item.id.toUpperCase() === product.id.toUpperCase()
  );
  if (!data || !data.priceText) return null;

  const priceText = data.priceText.replace(
    '{price}',
    priceFormat(product.price)
  );
  const paragraphs = priceText
    .split(/\n\s*\n/)
    .map(p => p.trim())
    .filter(Boolean);
  const heroImgSrc =
    '/images/pdp/' + product.extraInformation!.slug + '-priceheader.jpg?v=1';

  if (!isMobileDevice) {
    return (
      <Container className="justify-between px-0 md:px-4 md:flex md:flex-col md:pb-12 mt-[-20px]">
        <Container className=" justify-between py-8 px-0 md:px-4 md:flex md:flex-row md:items-start md:pb-4">
          <Container className="md:w-1/2 md:px-0 md:flex md:flex-col md:justify-start md:items-start mr-4">
            <Image
              src={
                '/images/pdp/' +
                product.extraInformation!.slug +
                '-priceheader-desktop.jpg?v=1'
              }
              width={1000}
              height={1000}
              priority
              alt="Powering beauty"
              className="w-full h-auto rounded-3xl"
            />
          </Container>
          <Container className="md:px-0 md:flex md:flex-col md:justify-start md:items-start w-full">
            <Title isAnimated size="2xl" className="font-bold mt-4">
              {data.priceTitle}
            </Title>
            <Container className="gap-16 justify-between py-8 px-0 md:px-4 md:flex md:flex-row md:items-start md:pb-4">
              <Container className="md:w-1/2 md:px-0 md:flex md:flex-col md:justify-start md:items-start">
                {paragraphs.map((p, idx) => (
                  <p key={idx} className="text-hg-black500 mb-4 last:mb-8">
                    {p}
                  </p>
                ))}
              </Container>

              <Container className="md:w-1/2 md:px-0 md:flex md:flex-col md:justify-start md:items-start">
                <Text
                  isAnimated
                  className="mb-4 text-hg-secondary font-gtUltra text-xldr"
                >
                  El precio incluye:
                </Text>
                <ul className="flex flex-col gap-3">
                  {data.priceBullets.map((item, idx) => (
                    <li
                      key={idx}
                      className="flex items-center gap-3 text-hg-black500 text-base"
                    >
                      <SvgCheck className="text-hg-secondary w-6 h-6 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </Container>
            </Container>

            {showButton && (
              <Flex
                layout="col-center"
                className="w-full px-4 py-8 md:px-0 md:py-12 flex justify-center items-center"
              >
                <ProductSelectorButton product={product} />
              </Flex>
            )}
          </Container>
        </Container>
      </Container>
    );
  }
  return (
    <div className="w-full">
      <div className="relative w-full mt-[-20px]">
        <Image
          src={heroImgSrc}
          width={1000}
          height={1000}
          priority
          alt="Powering beauty"
          className="w-full h-auto rounded-3xl"
        />
      </div>
      <Container className="md:px-0 md:flex md:flex-col md:justify-start md:items-start w-full">
        <Title isAnimated size="2xl" className="font-bold mt-4">
          {data.priceTitle}
        </Title>
      </Container>
      <Container className="gap-16 justify-between py-8 px-0 md:px-4 md:flex md:flex-row md:items-start md:pb-4 pdpExplanationTitle">
        <Container className="md:w-1/2 md:px-0 md:flex md:flex-col md:justify-start md:items-start">
          {paragraphs.map((p, idx) => (
            <p key={idx} className="text-hg-black500 mb-4 last:mb-8">
              {p}
            </p>
          ))}
        </Container>

        <Container className="md:w-1/2 md:px-0 md:flex md:flex-col md:justify-start md:items-start">
          <Text
            isAnimated
            className="mb-4 text-hg-secondary font-gtUltra text-xldr"
          >
            El precio incluye:
          </Text>
          <ul className="flex flex-col gap-3">
            {data.priceBullets.map((item, idx) => (
              <li
                key={idx}
                className="flex items-center gap-3 text-hg-black500 text-base"
              >
                <SvgCheck className="text-hg-secondary w-6 h-6 shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </Container>
      </Container>

      {showButton && (
        <Flex
          layout="col-center"
          className="w-full px-4 py-8 md:px-0 md:py-12 flex justify-center items-center"
        >
          <ProductSelectorButton product={product} />
        </Flex>
      )}
    </div>
  );
}
